import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { CalendarDays, X, Plus, User, Search, Clock, LogOut, History, Sparkles, Users } from 'lucide-react';
import {
  getMemberClasses, listActiveClasses, enrollMemberClass, unenrollMemberClass,
  MemberClass, MemberTrial, MemberPrevious, ActiveClass,
} from '../api';
import { useToast } from '../../../context/ToastContext';
import EmptyState from '../../../components/EmptyState';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';
import { horarioCorto } from '../../../utils/horario';
import { colorClase } from '../../../utils/classColor';

// Mismo peso que los títulos del perfil (negritas + línea): los grupos de clases se tienen que
// leer igual de fuerte que «Datos personales» (Carlos 15-ago).
const grupoTxt = 'flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content';
const grupoCls = `mb-2 border-b border-line pb-1.5 ${grupoTxt}`;

/** '2026-08-04' → '4 de agosto de 2026' (anclado a mediodía para que no se corra un día). */
function fechaLarga(iso: string) {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Modal de inscripción con BUSCADOR (Carlos 15-ago: el desplegable obligaba a ir localizando la
 *  clase a ojo). Filtra por nombre, estilo o profesor y se inscribe con un clic en la fila. */
function AddClassModal({ classes, onPick, onClose, busy }: {
  classes: ActiveClass[]; onPick: (c: ActiveClass) => void; onClose: () => void; busy: boolean;
}) {
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const filtradas = useMemo(() => {
    const t = norm(q.trim());
    if (!t) return classes;
    return classes.filter((c) => norm(`${c.name} ${c.style || ''} ${c.teacher || ''}`).includes(t));
  }, [q, classes]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-surface shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h3 className="font-display text-lg font-bold text-content">Inscribir a una clase</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="border-b border-line p-3">
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por clase, estilo o profesor…"
              className="w-full rounded-lg border border-line bg-surface py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {filtradas.length === 0 ? (
            <EmptyState compact icon={Search} title="Sin resultados"
              subtitle={classes.length === 0 ? 'Ya está inscrito en todas las clases vigentes.' : 'Prueba con otro nombre.'} />
          ) : (
            <ul className="space-y-1">
              {filtradas.map((c) => (
                <li key={c.class_id}>
                  {/* Mismo gris que las tarjetas de la ficha, con el color sólo en la franja. */}
                  <button disabled={busy} onClick={() => onPick(c)}
                    className="flex w-full items-center gap-3 overflow-hidden rounded-xl border border-line bg-canvas p-2.5 pl-0 text-left transition hover:border-accent/40 hover:bg-surface-2 disabled:opacity-40">
                    <span className="w-1.5 shrink-0 self-stretch" style={{ background: colorClase(c.color) }} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-content">{c.name}</span>
                      {/* Cupo y hora en vez del estilo (que repite el nombre de la clase) */}
                      <span className="flex flex-wrap items-center gap-x-2 text-xs text-content-3">
                        <span className="inline-flex items-center gap-1"><Users size={11} /> {c.enrolled ?? 0}{c.capacity ? `/${c.capacity}` : ''}</span>
                        {horarioCorto(c.schedule) && <span className="inline-flex items-center gap-1"><Clock size={11} /> {horarioCorto(c.schedule, ' · ')}</span>}
                        {c.teacher && <span>{c.teacher}</span>}
                      </span>
                    </span>
                    <Plus size={16} className="shrink-0 text-accent-ink" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t border-line px-4 py-2 text-xs text-content-3">
          {filtradas.length} clase{filtradas.length === 1 ? '' : 's'} disponible{filtradas.length === 1 ? '' : 's'}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function MemberClasses({ memberId, isAdmin }: { memberId: number; isAdmin: boolean }) {
  const toast = useToast();
  const [enrolled, setEnrolled] = useState<MemberClass[] | null>(() => readCache<MemberClass[]>(`member-classes:${memberId}`) ?? null);
  const [trials, setTrials] = useState<MemberTrial[]>([]);
  const [previous, setPrevious] = useState<MemberPrevious[]>([]);
  const [trialStyles, setTrialStyles] = useState<{ style: string; date: string; class_name: string | null }[]>([]);
  const [all, setAll] = useState<ActiveClass[]>(() => readCache<ActiveClass[]>('classes-cat') ?? []);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Clase cuyo «dar de baja» está esperando confirmación (null = ninguna).
  const [confirmar, setConfirmar] = useState<number | null>(null);

  function load() {
    getMemberClasses(memberId)
      .then((r) => { setEnrolled(r.data); setTrials(r.trials || []); setPrevious(r.previous || []); setTrialStyles(r.trial_styles || []); writeCache(`member-classes:${memberId}`, r.data); })
      .catch(() => setEnrolled((prev) => prev ?? []));
  }
  useEffect(() => {
    setEnrolled(readCache<MemberClass[]>(`member-classes:${memberId}`) ?? null);
    load();
    // Solo clases VIGENTES: inscribir a un ciclo terminado no le sirve a nadie.
    if (isAdmin) listActiveClasses({ vigentes: true }).then((r) => { setAll(r.data); writeCache('classes-cat', r.data); }).catch(() => {});
  }, [memberId, isAdmin]);

  const available = useMemo(() => {
    const ids = new Set((enrolled || []).map((c) => c.class_id));
    return all.filter((c) => !ids.has(c.class_id));
  }, [all, enrolled]);

  async function add(c: ActiveClass) {
    setBusy(true);
    try { await enrollMemberClass(memberId, c.class_id); setOpen(false); load(); toast.success(`Inscrito a ${c.name}`); }
    catch (e: any) { toast.error(e?.message || 'No se pudo inscribir'); }
    finally { setBusy(false); }
  }

  async function remove(c: MemberClass) {
    setBusy(true);
    try { await unenrollMemberClass(memberId, c.class_id); setConfirmar(null); load(); toast.success(`Dado de baja de ${c.name}`); }
    catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
    finally { setBusy(false); }
  }

  if (!enrolled) return <div className="mt-5 space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>;

  const actuales = enrolled.filter((c) => c.status !== 'archived');
  const anteriores = enrolled.filter((c) => c.status === 'archived');

  /** Una tarjeta de clase. `historica` = ciclo terminado: sin color vivo ni botón de baja. */
  const tarjeta = (c: MemberClass, historica: boolean) => {
    const col = colorClase(c.color);
    const horario = horarioCorto(c.schedule);
    return (
      <div key={c.class_id}
        className={`relative overflow-hidden rounded-xl border border-line bg-canvas shadow-sm transition hover:shadow-md ${historica ? 'opacity-75' : ''}`}>
        <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: col, opacity: historica ? 0.45 : 1 }} />
        <div className="relative flex items-start gap-3 py-3 pl-4 pr-2">
          <div className="min-w-0 flex-1">
            {/* La clase abre su ficha (video Karla 30-jul: «darle clic a Fit Program y que te abra la clase») */}
            <Link to={`/clases/${c.class_id}`}
              className="block truncate text-sm font-semibold text-content underline-offset-2 hover:underline">
              {c.name}
            </Link>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              {/* Cupo en vez del estilo: en «K-Pop Adultos» el estilo se llama igual que la clase y
                  parecía repetida (Carlos 15-ago). Inscritos / cupo sí es información útil. */}
              {c.enrolled != null && (
                <span title="Inscritos / cupo"
                  className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 font-medium text-content-2 ring-1 ring-line">
                  <Users size={11} /> {c.enrolled}{c.capacity ? `/${c.capacity}` : ''}
                  {!!c.capacity && Number(c.enrolled) >= Number(c.capacity) && <b className="text-amber-600 dark:text-amber-400">lleno</b>}
                </span>
              )}
              {/* El profesor lleva a SU ficha, no a la clase (Carlos 15-ago) */}
              {c.teacher && (c.teacher_id ? (
                <Link to={`/profesores/${c.teacher_id}`}
                  className="inline-flex items-center gap-1 text-content-2 underline-offset-2 hover:text-content hover:underline">
                  <User size={11} /> {c.teacher}
                </Link>
              ) : (
                <span className="inline-flex items-center gap-1 text-content-3"><User size={11} /> {c.teacher}</span>
              ))}
            </div>
            {horario && (
              <div className="mt-1 flex items-center gap-1 text-[11px] text-content-3">
                <Clock size={11} className="shrink-0" /> <span className="truncate">{horario}</span>
              </div>
            )}
          </div>
          {/* 🚨 Dar de baja PIDE CONFIRMACIÓN (Carlos 15-ago): la «X» muda se llevó por delante a
              una alumna de su clase de un clic y sin aviso. La pregunta sustituye al botón EN EL
              MISMO SITIO: si crece un bloque debajo, la tarjeta se estira y todo se te mueve.
              En clases ya terminadas no hay de qué dar de baja: no se ofrece. */}
          {isAdmin && !historica && (confirmar === c.class_id ? (
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="hidden text-[11px] font-medium text-content-2 sm:inline">¿Dar de baja?</span>
              <button onClick={() => remove(c)} disabled={busy}
                title={`Dar de baja de ${c.name}: deja de contar en la lista y en la asistencia`}
                className="rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-red-700 disabled:opacity-40">
                Sí, dar de baja
              </button>
              <button onClick={() => setConfirmar(null)} disabled={busy}
                className="rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-content-2 transition hover:bg-surface-2">
                No
              </button>
            </div>
          ) : (
            <button onClick={() => setConfirmar(c.class_id)} disabled={busy}
              className="shrink-0 rounded-lg border border-line p-1.5 text-content-3 transition hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-600 disabled:opacity-40 dark:hover:text-red-400"
              title="Dar de baja de esta clase" aria-label="Dar de baja de esta clase">
              <LogOut size={15} />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Título y acción en la MISMA fila y arriba del todo (Carlos 15-ago: el botón quedaba a
          media pantalla, debajo del encabezado de la pestaña). */}
      {/* El botón va ARRIBA del título, no a su lado: compartiendo renglón estiraba la fila y la
          línea del título bajaba, quedando desalineada con «Clases anteriores» y «Clases de
          prueba» (Carlos 15-ago). Así los tres encabezados tienen su línea a la misma altura. */}
      {isAdmin && (
        <div className="flex justify-end">
          <button onClick={() => setOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
            <Plus size={16} /> Añadir clase
          </button>
        </div>
      )}
      {open && <AddClassModal classes={available} busy={busy} onPick={add} onClose={() => setOpen(false)} />}

      <section>
        <h3 className={grupoCls}>
          <CalendarDays size={13} className="text-accent-ink" /> Clases inscritas ({actuales.length})
        </h3>
        {actuales.length === 0 ? (
          <EmptyState icon={CalendarDays} title="No está inscrito en clases"
            subtitle={isAdmin ? 'Usa el botón «Añadir clase».' : 'Sin clases asignadas.'} />
        ) : (
          <div className="space-y-2.5">{actuales.map((c) => tarjeta(c, false))}</div>
        )}
      </section>

      {/* ===== Clases ANTERIORES — ciclos ya archivados =====
          El backend siempre las devolvió, pero se pintaban mezcladas con las vigentes: 133 alumnos
          veían hasta 6 tarjetas iguales sin saber cuál está viva (audio de Carlos, 15-ago). */}
      {(anteriores.length > 0 || previous.length > 0) && (
        <section>
          <h4 className={grupoCls}>
            <History size={13} className="text-accent-ink" /> Clases anteriores ({anteriores.length + previous.length})
          </h4>
          <div className="space-y-2.5">
            {anteriores.map((c) => tarjeta(c, true))}
            {/* Asistió sin estar inscrita hoy (Carlos 15-ago / regla 15-sep): sale de las listas de
                Kydemy, así que muestra lo que de verdad tomó — no lo que alguien anotó. */}
            {previous.map((p) => (
              <div key={`prev-${p.class_id}`} className="relative overflow-hidden rounded-xl border border-line bg-canvas opacity-80">
                <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: colorClase(p.color), opacity: 0.45 }} />
                <div className="flex items-center justify-between gap-2 py-2.5 pl-4 pr-3">
                  <div className="min-w-0">
                    <Link to={`/clases/${p.class_id}`} className="block truncate text-sm font-semibold text-content underline-offset-2 hover:underline">{p.name}</Link>
                    <span className="text-[11px] text-content-3">{p.style ? `${p.style} · ` : ''}{p.attended} asistencia{p.attended === 1 ? '' : 's'} · última {fechaLarga(p.last_date)}</span>
                  </div>
                  <span className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium text-content-2 ring-1 ring-line">asistió sin inscripción</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ===== Clases de PRUEBA — derivadas de los pagos `voucher_trial` ===== */}
      {trials.length > 0 && (
        <section>
          <h4 className={grupoCls}>
            <Sparkles size={13} className="text-accent-ink" /> Clases de prueba y sueltas ({trials.length})
          </h4>
          {/* Regla (Karla 15-sep): la muestra GRATIS es una por estilo, de por vida, solo para alumnos.
              Aquí se ve qué estilos ya la gastaron; la suelta pagada no la consume. */}
          {trialStyles.length > 0 && (
            <p className="mb-2 text-[11px] text-content-3">
              Muestra gratis ya usada en: {trialStyles.map((t) => <b key={t.style} className="text-content">{t.style}</b>).reduce((a: any[], x, i) => (i ? [...a, ', ', x] : [x]), [])}
            </p>
          )}
          <div className="space-y-2">
            {trials.map((t) => {
              const probable = !t.class_name && enrolled.length === 1 ? enrolled[0] : null;
              return (
              <div key={t.payment_id}
                className="relative overflow-hidden rounded-xl border border-dashed border-line bg-canvas px-4 py-2.5">
                <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: colorClase(t.color ?? probable?.color), opacity: 0.6 }} />
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    {t.class_id && t.class_name ? (
                      <Link to={`/clases/${t.class_id}`}
                        title={t.confirmed ? 'Clase confirmada a mano: Kydemy no guardó de cuál fue la prueba' : undefined}
                        className="block truncate text-sm font-medium text-content underline-offset-2 hover:underline">{t.class_name}</Link>
                    ) : t.class_name ? (
                      <span className="block truncate text-sm font-medium text-content">{t.class_name}</span>
                    ) : probable ? (
                      // Kydemy guardó la prueba sin decir de qué clase (`{NULL}`). Si el alumno sólo
                      // tiene UNA clase, casi seguro es esa — pero se marca como «probable», no se
                      // afirma: es una deducción nuestra, no un dato del sistema.
                      <span className="block truncate text-sm font-medium text-content"
                        title="Kydemy no guardó de qué clase fue la prueba; se deduce porque es la única clase del alumno">
                        {probable.name} <span className="font-normal text-content-3">(probable)</span>
                      </span>
                    ) : (
                      <span className="block truncate text-sm font-medium text-content-2"
                        title="Kydemy no guardó de qué clase fue esta prueba">Clase sin especificar</span>
                    )}
                    <span className="text-[11px] text-content-3">{fechaLarga(t.date)}{t.comment ? ` · ${t.comment}` : ''}</span>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${t.kind === 'muestra' ? 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/30 dark:text-emerald-400' : 'bg-surface text-content-2 ring-line'}`}>
                    {t.kind === 'muestra' ? 'muestra gratis' : `suelta $${Number(t.amount).toFixed(0)}`}{t.style ? ` · ${t.style}` : ''}
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
